import { useCallback, useEffect, useRef, useState } from "react";

const LS_KEY = "pgt_design_v2";

function defaultTrack(tenant) {
  const mark = (tenant?.graphic_colours || []).find((c) => c.available) || { hex: "#f5f5f0" };
  return { layout: "single", lanes: 1, markings: { type: "none", interval: 1, position: "centre", lane_mode: "shared", colour: mark.hex, start: false, finish: false, start_finish_text: false } };
}

export function emptyDesign(tenant) {
  const lim = tenant?.size_limits || { min_length: 1, max_length: 12, min_width: 1, max_width: 2 };
  const turf = (tenant?.turf_colours || []).find((c) => c.available) || { name: "Jet Black", hex: "#111111" };
  const sec = (tenant?.turf_colours || []).filter((c) => c.available)[2] || { name: "Performance Green", hex: "#1f7a3d" };
  const bw = tenant?.track_config?.border_widths || { standard: 0.1 };
  return {
    dimensions: { length: Math.min(10, lim.max_length), width: Math.min(1.5, lim.max_width) },
    base_style: { category: "Solid", pattern: "solid", primary: turf.hex, primary_name: turf.name, secondary: sec.hex, secondary_name: sec.name, scale: "medium" },
    turf_colour_name: turf.name, turf_colour_hex: turf.hex,
    track: defaultTrack(tenant),
    border: { enabled: false, colour: (tenant?.graphic_colours || [])[0]?.hex || "#f5f5f0", colour_name: "White", width_key: "standard", width_m: bw.standard || 0.1 },
    options: { marking_colour: (tenant?.graphic_colours || [])[0]?.hex || "#f5f5f0", metre_markings: "none" },
    objects: [],
    complex_artwork: false,
    mode: "manual",
  };
}

function normalize(d, tenant) {
  const base = emptyDesign(tenant);
  if (!d.base_style) d.base_style = base.base_style;
  if (!d.base_style.scale) d.base_style.scale = "medium";
  if (!d.track) d.track = base.track;
  if (!d.border) d.border = base.border;
  if (!d.options) d.options = base.options;
  if (!d.dimensions) d.dimensions = base.dimensions;
  if (!d.objects) d.objects = [];
  // explicit active mode is the single source of truth; infer for legacy persisted designs
  if (d.mode !== "ai" && d.mode !== "manual") d.mode = d.ai_artwork ? "ai" : "manual";
  return d;
}

// Remove every AI-session artefact from the ACTIVE design (does NOT delete stored AI masters).
function stripAi(d) {
  delete d.ai_artwork; delete d.ai; delete d.is_ai_design; delete d.locked;
  d.mode = "manual";
  return d;
}

export function useDesigner(tenant) {
  const [design, setDesignState] = useState(() => {
    try { const s = localStorage.getItem(LS_KEY); if (s) return normalize(JSON.parse(s), tenant); } catch (e) {}
    return emptyDesign(tenant);
  });
  // Selection is a SET of element ids: object ids and `track:<key>` for generated markings.
  // selectedId (single) is derived so all existing single-element toolbars/controls keep working.
  const [selectedIds, setSelectedIds] = useState([]);
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  const setSelectedId = useCallback((id) => setSelectedIds(id ? [id] : []), []);
  const toggleSelected = useCallback((id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])), []);
  const past = useRef([]);
  const future = useRef([]);
  const [, force] = useState(0);

  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(design)); } catch (e) {} }, [design]);

  const latest = useRef(design);
  latest.current = design;
  // History pushes happen OUTSIDE setState updaters (updaters are double-invoked in StrictMode).
  const pushHistory = useCallback(() => {
    past.current.push(JSON.stringify(latest.current));
    if (past.current.length > 60) past.current.shift();
    future.current = [];
  }, []);
  const commit = useCallback((updater) => {
    pushHistory();
    const next = typeof updater === "function" ? updater(latest.current) : updater;
    latest.current = next;
    setDesignState(next);
    force((n) => n + 1);
  }, [pushHistory]);

  const setDimensions = useCallback((dims) => commit((p) => ({ ...p, dimensions: { ...p.dimensions, ...dims } })), [commit]);

  // Lane count (1–5) is customer-chosen and independent of turf width. Keeps `layout` in sync for
  // backward compatibility (single = 1 lane / whole turf; two_lane = 2+ lanes).
  const setLanes = useCallback((n) => commit((p) => {
    const lanes = Math.max(1, Math.min(5, Number(n) || 1));
    return { ...p, track: { ...p.track, lanes, layout: lanes >= 2 ? "two_lane" : "single" } };
  }), [commit]);

  const setBaseStyle = useCallback((patch) => commit((p) => {
    const bs = { ...p.base_style, ...patch };
    const next = { ...p, base_style: bs };
    if (bs.category === "Solid") { next.turf_colour_hex = bs.primary; next.turf_colour_name = bs.primary_name; }
    return next;
  }), [commit]);

  const setLayout = useCallback((layout) => commit((p) => ({ ...p, track: { ...p.track, layout } })), [commit]);
  const setBorder = useCallback((patch) => commit((p) => ({ ...p, border: { ...p.border, ...patch } })), [commit]);
  // Regenerating markings (type/interval/position/lane mode) clears per-element deletions so the new set is complete.
  const setMarkings = useCallback((patch) => commit((p) => {
    const regen = ["type", "interval", "position", "lane_mode"].some((k) => k in patch && patch[k] !== p.track.markings[k]);
    const track = { ...p.track, markings: { ...p.track.markings, ...patch } };
    if (regen) track.hidden = [];
    return { ...p, track, options: { ...p.options, marking_colour: patch.colour || p.options.marking_colour } };
  }), [commit]);
  const setMarkingColour = useCallback((hex) => setMarkings({ colour: hex }), [setMarkings]);

  const addObject = useCallback((obj) => {
    const id = "o_" + Math.random().toString(36).slice(2, 9);
    const full = { id, x: 0.5, y: 0.5, scale: 0.14, rotation: 0, layer: 1, ...obj };
    commit((p) => ({ ...p, objects: [...p.objects, full] }));
    setSelectedId(id);
    return id;
  }, [commit]);

  // Push the current state onto history without changing it (call before a live drag/resize gesture)
  const checkpoint = useCallback(() => { pushHistory(); force((n) => n + 1); }, [pushHistory]);
  const updateObject = useCallback((id, patch, record = true) => {
    const up = (p) => ({ ...p, objects: p.objects.map((o) => (o.id === id ? { ...o, ...patch } : o)) });
    if (record) commit(up);
    else { latest.current = up(latest.current); setDesignState(latest.current); }
  }, [commit]);

  const removeObject = useCallback((id) => { commit((p) => ({ ...p, objects: p.objects.filter((o) => o.id !== id) })); setSelectedId(null); }, [commit, setSelectedId]);
  // Delete ONE generated marking/number/line by key: nothing else is regenerated or moved.
  const hideTrackPrim = useCallback((key) => { commit((p) => ({ ...p, track: { ...p.track, hidden: [...(p.track.hidden || []), key] } })); setSelectedId(null); }, [commit, setSelectedId]);
  // Delete any mix of objects and generated markings in ONE undoable step; nothing else is touched.
  const removeSelected = useCallback((ids) => {
    const objIds = new Set(ids.filter((i) => !String(i).startsWith("track:")));
    const keys = ids.filter((i) => String(i).startsWith("track:")).map((i) => i.slice(6));
    if (!objIds.size && !keys.length) return;
    commit((p) => ({ ...p, objects: p.objects.filter((o) => !objIds.has(o.id)),
      track: keys.length ? { ...p.track, hidden: [...(p.track.hidden || []), ...keys] } : p.track }));
    setSelectedIds([]);
  }, [commit]);
  const duplicateObject = useCallback((id) => commit((p) => {
    const o = p.objects.find((x) => x.id === id); if (!o) return p;
    const copy = { ...o, id: "o_" + Math.random().toString(36).slice(2, 9), x: Math.min(0.9, o.x + 0.05), y: Math.min(0.9, o.y + 0.05) };
    return { ...p, objects: [...p.objects, copy] };
  }), [commit]);

  const undo = useCallback(() => { if (!past.current.length) return; future.current.push(JSON.stringify(latest.current)); latest.current = JSON.parse(past.current.pop()); setDesignState(latest.current); setSelectedIds([]); force((n) => n + 1); }, [setSelectedIds]);
  const redo = useCallback(() => { if (!future.current.length) return; past.current.push(JSON.stringify(latest.current)); latest.current = JSON.parse(future.current.pop()); setDesignState(latest.current); setSelectedIds([]); force((n) => n + 1); }, [setSelectedIds]);
  const reset = useCallback(() => { commit(emptyDesign(tenant)); setSelectedId(null); }, [commit, tenant]);
  const loadDesign = useCallback((d) => { commit(normalize(d, tenant)); setSelectedId(null); }, [commit, tenant]);

  const applyTemplate = useCallback((tpl) => commit((p) => {
    const objects = (tpl.objects || []).map((o) => ({ ...o, id: "o_" + Math.random().toString(36).slice(2, 9) }));
    const next = stripAi({ ...p, objects });   // selecting a manual template ALWAYS leaves AI mode
    if (tpl.base) next.base_style = { ...p.base_style, ...tpl.base };
    if (tpl.track) {
      let track = { ...defaultTrack(tenant), ...tpl.track, markings: { ...defaultTrack(tenant).markings, ...(tpl.track.markings || {}), colour: p.track.markings.colour } };
      track.lanes = tpl.track.lanes || (track.layout === "two_lane" ? 2 : 1);
      track.layout = track.lanes >= 2 ? "two_lane" : "single";
      next.track = track;
    }
    return next;
  }), [commit, tenant]);

  return {
    design, selectedId, setSelectedId, selectedIds, setSelectedIds, toggleSelected, removeSelected, setDesignState, checkpoint,
    setDimensions, setBaseStyle, setLayout, setBorder, setMarkings, setMarkingColour, setLanes,
    addObject, updateObject, removeObject, duplicateObject, hideTrackPrim,
    undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0,
    reset, loadDesign, applyTemplate,
  };
}
